import type { StoreApi } from 'zustand';
import type { Prettify } from '../../../../../lib/typeUtils';

/**
 * Pre-computed spatial context attached to every dispatched event.
 * Computed once in the hub — handlers read it instead of doing their own
 * `element.contains()` checks.
 */
export type EventOrigin = {
  /** `true` when the event target is inside (or is) the canvas SVG viewport. */
  insideCanvas: boolean;
};

type CanvasInputEventBase = {
  origin: EventOrigin;
};

export type CanvasInputEvent = Prettify<
  CanvasInputEventBase &
    (
      | {
          type: 'pointerDown' | 'pointerMove' | 'pointerUp' | 'pointerCancel';
          pointerId: number;
          pointerType: string;
          rawEvent: PointerEvent;
          clientX: number;
          clientY: number;
        }
      | {
          type: 'wheel';
          rawEvent: WheelEvent;
          deltaX: number;
          deltaY: number;
          deltaMode: number;
          clientX: number;
          clientY: number;
        }
      | {
          type: 'keyDown' | 'keyUp';
          rawEvent: KeyboardEvent;
          key: string;
          code: string;
          ctrlKey: boolean;
          shiftKey: boolean;
          altKey: boolean;
          metaKey: boolean;
        }
    )
>;

export type CanvasHandlerExecutionContext<TStore = unknown> = {
  store: StoreApi<TStore>;
};

export type CanvasHandlerResult = {
  handled?: boolean;
  stopPropagation?: boolean;
  startLock?: boolean;
  endLock?: boolean;
} | void;

// Bivariant callback helpers (same pattern as HandlerCreator)
type BivariantEventCallback<TStore, TResult> = {
  bivarianceHack(
    event: CanvasInputEvent,
    ctx: CanvasHandlerExecutionContext<TStore>
  ): TResult;
}['bivarianceHack'];

type BivariantLifecycleCallback<TStore, TResult> = {
  bivarianceHack(ctx: CanvasHandlerExecutionContext<TStore>): TResult;
}['bivarianceHack'];

export type CanvasHandler<TStore = unknown, TId extends string = string> = {
  id: TId;
  shouldRun?: BivariantEventCallback<TStore, boolean>;
  handleEvent: BivariantEventCallback<TStore, CanvasHandlerResult>;
  onMount?: BivariantLifecycleCallback<TStore, (() => void) | void>;
  onUnmount?: () => void;
};

// Type guards
export const hasOnUnmount = <TStore>(
  handler: CanvasHandler<TStore>
): handler is CanvasHandler<TStore> & { onUnmount: () => void } =>
  handler.onUnmount !== undefined;
